import {
  ACCESSIBILITY_ARGS,
  ALL_LAYOUT_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { concat, fn, hash } from '@ember/helper';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import CharacterCounter from '../../core/character-counter.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from '../../action/button/ghost.gjs';
import InputBase from '../../core/input.gjs';
import ShiftFocusToParent from '../../../../modifiers/shift-focus-to-parent';
import Super from '../../core/base.gjs';
import styles from './field.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const ClearButton = <template>
  <GhostButton
    @id={{@id}}
    @registerComponent={{@registerComponent}}
    @unregisterComponent={{@unregisterComponent}}
    class="spds-menu-search-input-clear"
    tabindex="-1"
    {{this.setupComponent}}
    ...attributes
  >
    {{svgJar "x-mark" class="spds-menu-search-input-clear-icon"}}
  </GhostButton>
</template>;

const Icon = <template>
  {{svgJar
    (or @name "search")
    class=(concat "spds-menu-search-input-icon " (or @class ""))
    data-test-spds-menu-search-input-icon=""
  }}
</template>;

const InputComponent = <template>
  <InputBase
    @ariaLabel={{@ariaLabel}}
    @autofocus={{@autofocus}}
    @disabled={{@disabled}}
    @id={{@id}}
    @maxLength={{@maxLength}}
    @noBorder={{true}}
    @onInput={{@onInput}}
    @placeholder={{@placeholder}}
    @registerComponent={{@registerComponent}}
    @required={{@required}}
    @type="text"
    @unregisterComponent={{@unregisterComponent}}
    @value={{@value}}
    class="spds-menu-search-input-field"
    {{ShiftFocusToParent}}
    ...attributes
  />
</template>;

const METADATA = Metadata.define({
  description: `Presentational search field row shared by the search trigger and the in-menu search.

Yields contextual \`Icon\`, \`Input\`, and \`ClearButton\` building blocks so consumers can compose the
same row markup while owning their own container chrome, test hooks, and behavior (open/close, focus,
filtering). This keeps the visible search field consistent without duplicating the markup.`,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'ariaLabel', defaultValue: 'Filter list' }]),
    pick(ALL_LAYOUT_ARGS, [{ name: 'gap', defaultValue: '8px' }]),
    pick(INPUT_FORM_ARGS, ['charLimit', 'hideCharacterCounter', 'value']),
  ],
  name: 'Search Input',
});

export default class SpdsInputSearchField extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-search-input';
  styles = styles;

  @tracked clearButtonComponent;
  @tracked inputComponent;

  get ariaLabel() {
    return this.argOrDefault('ariaLabel');
  }

  get characterCounterId() {
    return this.composeSubcomponentId('character-counter');
  }

  get clearButtonElement() {
    return this.clearButtonComponent?.baseElement;
  }

  get clearButtonId() {
    return this.composeSubcomponentId('clear-button');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get hideCharacterCounter() {
    return this.argOrDefault('hideCharacterCounter');
  }

  get inputElement() {
    return this.inputComponent?.baseElement;
  }

  get inputId() {
    return this.composeSubcomponentId('input');
  }

  get showCharacterCounter() {
    return !this.hideCharacterCounter && this.args.charLimit > 0;
  }

  <template>
    <FlexRow
      @alignItems="center"
      @flex="1"
      @gap={{this.gap}}
      @id={{this.id}}
      @justifyContent="center"
      class={{this.classes}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Icon=Icon
          Input=(component
            InputComponent
            ariaLabel=this.ariaLabel
            id=this.inputId
            maxLength=@charLimit
            registerComponent=(fn this.registerComponent "inputComponent")
            unregisterComponent=(fn this.unregisterComponent "inputComponent")
            value=@value
          )
          ClearButton=(component
            ClearButton
            id=this.clearButtonId
            registerComponent=(fn this.registerComponent "clearButtonComponent")
            unregisterComponent=(fn this.unregisterComponent "clearButtonComponent")
          )
        )
      }}
    </FlexRow>
    {{#if this.showCharacterCounter}}
      <FlexRow @justifyContent="flex-end">
        <CharacterCounter
          @id={{this.characterCounterId}}
          @limit={{@charLimit}}
          @value={{@value}}
          class="spds-menu-search-input-char-count"
          data-test-spds-search-input-char-count={{this.id}}
        />
      </FlexRow>
    {{/if}}
  </template>
}

export const SEARCH_INPUT_METADATA = SpdsInputSearchField.metadata;
